import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Loader2, ArrowRight, ArrowDown, ArrowUp, Minus, AlertCircle, CheckCircle2, AlertTriangle, Clock, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusIcons = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
  pending: Clock,
};

function MetricDiff({ label, valA, valB, unit = "", higherIsBetter = true }) {
  const diff = (valB ?? 0) - (valA ?? 0);
  const improved = higherIsBetter ? diff > 0 : diff < 0;
  const worse = higherIsBetter ? diff < 0 : diff > 0;
  const absDiff = Math.abs(Math.round(diff * 10) / 10);

  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
      <div className="flex items-center gap-4 font-mono text-sm">
        <span>{valA ?? "-"}{unit}</span>
        <span className="text-muted-foreground">vs</span>
        <span>{valB ?? "-"}{unit}</span>
        {diff !== 0 && (
          <Badge
            variant="outline"
            className={`text-[10px] gap-0.5 ${improved ? "text-green-700 bg-green-50 border-green-200" : worse ? "text-red-700 bg-red-50 border-red-200" : ""}`}
          >
            {diff > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {absDiff}{unit}
          </Badge>
        )}
      </div>
    </div>
  );
}

function TraceColumn({ trace, label }) {
  if (!trace) return null;
  const StatusIcon = statusIcons[trace.status] || Clock;

  return (
    <div className="space-y-4" data-testid={`compare-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="outline" className="font-mono text-xs">{label}</Badge>
        <StatusIcon className="w-4 h-4 text-muted-foreground" />
        <Badge variant="secondary" className="font-mono text-xs">
          {trace.evaluation?.overall_score ?? "-"}/100
        </Badge>
      </div>

      {/* Query */}
      <Card className="border border-border shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Query</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">{trace.user_query}</p>
        </CardContent>
      </Card>

      {/* Model & Metadata */}
      <div className="grid grid-cols-3 gap-2">
        <div className="border border-border rounded-sm p-2 text-center">
          <div className="text-xs text-muted-foreground">Model</div>
          <div className="text-sm font-mono font-bold">{trace.model_name}</div>
        </div>
        <div className="border border-border rounded-sm p-2 text-center">
          <div className="text-xs text-muted-foreground">Latency</div>
          <div className="text-sm font-mono font-bold">{trace.latency_ms}ms</div>
        </div>
        <div className="border border-border rounded-sm p-2 text-center">
          <div className="text-xs text-muted-foreground">Chunks</div>
          <div className="text-sm font-mono font-bold">{trace.retrieved_chunks?.length ?? 0}</div>
        </div>
      </div>

      {/* Response */}
      <Card className="border border-border shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Response</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="max-h-[200px]">
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{trace.model_response}</p>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Chunks */}
      <Card className="border border-border shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Retrieved Chunks ({trace.retrieved_chunks?.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="max-h-[200px]">
            <div className="space-y-2">
              {trace.retrieved_chunks?.map((chunk, i) => (
                <div key={i} className="border border-border rounded-sm p-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Chunk {i}</span>
                    <Badge variant="outline" className="font-mono text-[10px]">{Math.round(chunk.score * 100)}%</Badge>
                  </div>
                  <p className="text-xs line-clamp-3">{chunk.content}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Issues */}
      {trace.evaluation?.issues?.length > 0 && (
        <Card className="border border-red-200 shadow-none bg-red-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-red-700">
              Issues ({trace.evaluation.issues.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {trace.evaluation.issues.map((issue, i) => (
                <div key={i} className="text-xs">
                  <Badge variant="outline" className={`text-[10px] mr-1 ${issue.severity === "high" ? "text-red-700 border-red-300" : issue.severity === "medium" ? "text-yellow-700 border-yellow-300" : "text-muted-foreground"}`}>
                    {issue.severity}
                  </Badge>
                  {issue.description}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function CompareRunsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  const traceIdA = searchParams.get("a");
  const traceIdB = searchParams.get("b");
  const projectId = searchParams.get("project");

  const fetchComparison = useCallback(async () => {
    if (!traceIdA || !traceIdB) {
      toast.error("Select two traces to compare");
      navigate(-1);
      return;
    }
    try {
      const { data } = await axios.post(
        `${API_URL}/api/v1/traces/compare`,
        { trace_id_a: traceIdA, trace_id_b: traceIdB },
        { withCredentials: true }
      );
      setComparison(data);
    } catch (error) {
      toast.error("Failed to load comparison");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [traceIdA, traceIdB, navigate]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!comparison) return null;

  const { trace_a, trace_b, diff } = comparison;

  return (
    <div className="min-h-screen bg-background" data-testid="compare-runs-page">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/dashboard">Dashboard</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            {projectId && (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild><Link to={`/projects/${projectId}`}>Project</Link></BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem>
              <BreadcrumbPage>Compare Runs</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Title */}
        <div className="flex items-center gap-3 mb-6">
          <GitCompareArrows className="w-6 h-6 text-muted-foreground" />
          <h1 className="text-2xl font-black tracking-tight font-heading">Compare Runs</h1>
        </div>

        {/* Diff Summary */}
        <Card className="border border-border shadow-none mb-6" data-testid="diff-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <MetricDiff label="Overall Score" valA={diff.score_a} valB={diff.score_b} />
            <MetricDiff label="Faithfulness" valA={diff.faithfulness_a} valB={diff.faithfulness_b} />
            <MetricDiff label="Latency" valA={diff.latency_a} valB={diff.latency_b} unit="ms" higherIsBetter={false} />
            <MetricDiff label="Chunks Retrieved" valA={diff.chunk_count_a} valB={diff.chunk_count_b} />
            {diff.confidence_a && diff.confidence_b && (
              <>
                <MetricDiff label="Lexical Overlap" valA={diff.confidence_a.lexical_overlap} valB={diff.confidence_b.lexical_overlap} unit="%" />
                <MetricDiff label="Semantic Similarity" valA={diff.confidence_a.semantic_similarity} valB={diff.confidence_b.semantic_similarity} unit="%" />
                <MetricDiff label="Grounding Strength" valA={diff.confidence_a.grounding_strength} valB={diff.confidence_b.grounding_strength} unit="%" />
              </>
            )}
          </CardContent>
        </Card>

        {/* Side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TraceColumn trace={trace_a} label="Run A" />
          <TraceColumn trace={trace_b} label="Run B" />
        </div>

        {/* View individually */}
        <div className="flex gap-4 mt-6 justify-center">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/traces/${trace_a.id}`}>
              View Run A Detail <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/traces/${trace_b.id}`}>
              View Run B Detail <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
